import { computed } from 'vue';
import { defineStore } from 'pinia';
import { useQuery } from '@tanstack/vue-query';
import textRepository from '../repositories/text_repository';

export const useTextStore = defineStore('texts', () => {
    // TanStack Query setup
    const TEXTS_QUERY_KEY = ['texts', 'direct-deposit'] as const;

    // Query for fetching texts
    const textsQuery = useQuery({
        queryKey: TEXTS_QUERY_KEY,
        queryFn: async () => {
            const response = await textRepository.texts.textsList({ appId: 'direct-deposit' });
            return response.data;
        },
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        enabled: false
    });

    // Computed values for easy access
    const texts = computed(() => textsQuery.data.value ?? []);
    const isLoading = computed(() => textsQuery.isPending.value);
    const isLoaded = computed(() => textsQuery.isSuccess.value);
    const error = computed(() => textsQuery.error.value);

    // Methods
    const fetchTexts = async () => {
        try {
            const result = await textsQuery.refetch();
            return result.data;
        } catch (error) {
            console.error('Error fetching text:', error);
            // Don't throw error to allow fallback text to work
        }
    };

    function getText(textId: string, defaultText: string): string {
        // Auto-fetch texts if not loaded yet
        if (!isLoaded.value && !isLoading.value) {
            fetchTexts();
        }

        const foundText = texts.value.find((text) => text.textId === textId);
        return foundText ? foundText.text : defaultText;
    }

    return {
        // Data
        texts,
        // States
        isLoaded,
        isLoading,
        error,
        // Methods
        fetchTexts,
        getText,
        // Expose query for direct access if needed
        refetchTexts: textsQuery.refetch
    };
});
